import React, { useState, useCallback } from 'react';
import { Upload, FileMusic, AlertCircle } from 'lucide-react';
import { Midi } from '@tonejs/midi';

/**
 * MIDI File Upload Component
 * Handles file upload and initial parsing
 */
export default function MidiFileUpload({ onMidiUpload }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;

    // Validate file type
    if (!file?.name?.toLowerCase()?.endsWith('.mid') && !file?.name?.toLowerCase()?.endsWith('.midi')) {
      setError('Please upload a valid MIDI file (.mid or .midi)');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Read and parse MIDI file
      const arrayBuffer = await file?.arrayBuffer();
      const midi = new Midi(arrayBuffer);

      if (!midi?.tracks || midi?.tracks?.length === 0) {
        throw new Error('No tracks found in MIDI file');
      }

      // Pass midi data and file to parent
      onMidiUpload({ midi, file });
    } catch (err) {
      console.error('Error parsing MIDI:', err);
      setError('Failed to parse MIDI file. Please ensure it\'s a valid MIDI file.');
    } finally {
      setIsProcessing(false);
    }
  }, [onMidiUpload]);

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    setIsDragging(false);

    const file = e?.dataTransfer?.files?.[0];
    handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e?.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e?.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e) => {
    const file = e?.target?.files?.[0];
    handleFile(file);
  }, [handleFile]);

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <Upload className="w-7 h-7 text-purple-600" />
        Upload MIDI File
      </h2>

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-4 border-dashed rounded-2xl p-12 transition-all ${
          isDragging
            ? 'border-purple-500 bg-purple-50' :'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
        }`}
      >
        <div className="text-center">
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
              <p className="text-lg text-gray-600">Processing MIDI file...</p>
            </>
          ) : (
            <>
              <FileMusic className="w-20 h-20 text-purple-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Drop your MIDI file here
              </h3>
              <p className="text-gray-600 mb-6">or click to browse</p>

              <label className="inline-block">
                <input
                  type="file"
                  accept=".mid,.midi"
                  onChange={handleFileInput}
                  className="hidden"
                />
                <span className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold cursor-pointer hover:shadow-lg transition-all inline-block">
                  Select MIDI File
                </span>
              </label>

              <p className="text-sm text-gray-500 mt-6">
                Supported formats: .mid, .midi
              </p>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Error</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 p-6 bg-blue-50 rounded-xl">
        <h4 className="font-bold text-gray-800 mb-3">📝 Instructions:</h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>• Upload a MIDI file containing the melody line students should sing</li>
          <li>• The file should contain clear monophonic vocal melody</li>
          <li>• You'll be able to select the specific track after upload</li>
          <li>• Best results with MIDI files exported from notation software</li>
        </ul>
      </div>
    </div>
  );
}